import { FC, useRef } from 'react'

import { Trash } from '@syconium/little-miss-figgy'

import { useTranslation } from '../../../../../../app/_providers/TranslationProvider.client'
import { PlacementId } from '../../../../../../containers/EmbroideryContainer/combination-rules/placementIds'
import { TextItemValues } from '../../../../../../containers/EmbroideryContainer/providers/types'
import { EmbroideryTextInput } from '../../../primitives/EmbroideryTextInput/EmbroideryTextInput'

import { LineField, LineFieldList, LineHeader, LineLabel, LineTrash } from './styles'
import { textLimitsFor } from './textLimits'

export type TextLineFieldsProps = {
  placementId: PlacementId
  values: TextItemValues
  onChange(values: TextItemValues): void
  errors?: { firstLine?: string; secondLine?: string }
  disabled?: boolean
}

export const TextLineFields: FC<TextLineFieldsProps> = ({
  placementId,
  values,
  onChange,
  errors,
  disabled,
}) => {
  const {
    embroidery: {
      modal: {
        forms: { textFields: copy, textOption },
      },
    },
  } = useTranslation()

  const { lineCount, maxCharactersPerLine } = textLimitsFor(placementId)
  const listRef = useRef<HTMLDivElement>(null)

  const lines = [
    {
      key: 'firstLine' as const,
      label: copy.firstLineLabel,
      placeholder: textOption.firstLinePlaceholder,
      value: values.firstLine,
      error: errors?.firstLine,
    },
    {
      key: 'secondLine' as const,
      label: copy.secondLineLabel,
      placeholder: textOption.secondLinePlaceholder,
      value: values.secondLine ?? '',
      error: errors?.secondLine,
    },
  ].slice(0, lineCount)

  return (
    <LineFieldList ref={listRef}>
      {lines.map(line => {
        const labelId = `embroidery-text-${placementId}-${line.key}`

        return (
          <LineField key={line.key}>
            <LineHeader>
              <LineLabel id={labelId} disabled={disabled}>
                {line.label}
              </LineLabel>

              {line.value.length > 0 && !disabled && (
                <LineTrash
                  type='button'
                  aria-label={copy.clearLine.replace('{{line}}', line.label)}
                  onClick={() => {
                    // The trash button unmounts once the line is empty; keep focus in the line.
                    listRef.current
                      ?.querySelector<HTMLInputElement>(`input[name="${line.key}"]`)
                      ?.focus()
                    onChange({ ...values, [line.key]: '' })
                  }}
                >
                  <Trash height='16px' width='16px' />
                </LineTrash>
              )}
            </LineHeader>

            <EmbroideryTextInput
              ariaLabelledby={labelId}
              disabled={disabled}
              errorMessage={line.error}
              maxCharacterCount={maxCharactersPerLine}
              name={line.key}
              placeholder={line.placeholder}
              value={line.value}
              onChange={event => onChange({ ...values, [line.key]: event.target.value })}
            />
          </LineField>
        )
      })}
    </LineFieldList>
  )
}
